import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { supabase } from "../lib/supabaseClient";
import { FiDownload, FiEye } from "react-icons/fi";
import { downloadAuctionProposal } from "../lib/auctionProposalGenerator";

const localeFromLanguage = (language) => {
  const code = String(language || "fr").split("-")[0];
  if (code === "de") return "de-DE";
  if (code === "en") return "en-GB";
  return "fr-FR";
};

const AuctionProposal = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.language);

  const [isLoading, setIsLoading] = useState(true);
  const [artwork, setArtwork] = useState(null);
  const [formData, setFormData] = useState({
    auctionHouse: location.state?.institution || "",
    message: "",
    email: "",
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    const fetchArtwork = async () => {
      if (!artworkId) return;

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("artworks")
          .select("*")
          .eq("id", artworkId)
          .single();

        if (error) throw error;

        const parsed = {
          ...data,
          tags: data.tags || [],
          documents:
            typeof data.documents === "string"
              ? JSON.parse(data.documents)
              : data.documents || [],
          images:
            typeof data.images === "string"
              ? JSON.parse(data.images)
              : data.images || [],
          provenance:
            typeof data.provenance === "string"
              ? JSON.parse(data.provenance)
              : data.provenance || [],
          specifications:
            typeof data.specifications === "string"
              ? JSON.parse(data.specifications)
              : data.specifications || {
                  dimensions: { height: 0, width: 0, depth: 0, unit: "cm" },
                  framedSize: null,
                  paperSize: null,
                  duration: "",
                },
          acquisitionDetails:
            typeof data.acquisitionDetails === "string"
              ? JSON.parse(data.acquisitionDetails)
              : data.acquisitionDetails || { source: "", date: "", price: "" },
          value: data.value || 0,
          insured: data.insured || false,
          insuranceDetails: data.insuranceDetails || null,
        };

        setArtwork(parsed);

        if (location.state) {
          setFormData((prev) => ({
            ...prev,
            auctionHouse: location.state.institution || prev.auctionHouse,
          }));
        }
      } catch (err) {
        console.error("Error loading artwork:", err);
        toast.error(t("auctionProposalPage.errors.loadArtwork"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchArtwork();
  }, [artworkId, location.state, t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePreview = () => {
    setPreviewMode(true);
  };

  const handleDownload = async () => {
    if (!artwork) return;

    try {
      setPdfGenerating(true);
      const safeTitle = artwork.title || t("auctionProposalPage.fallbacks.artworkFile");
      const safeAuctionHouse = formData.auctionHouse || t("auctionProposalPage.fallbacks.auctionHouseFile");
      await downloadAuctionProposal(
        artwork,
        formData.auctionHouse,
        formData.message,
        `${t("auctionProposalPage.pdf.filePrefix")}-${safeTitle}-${safeAuctionHouse}.pdf`
      );
    } catch (error) {
      console.error("Error generating auction proposal PDF:", error);
      toast.error(t("auctionProposalPage.errors.pdf"));
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!artwork || !formData.auctionHouse) {
      toast.warning(t("auctionProposalPage.errors.required"));
      return;
    }

    try {
      const { data, error } = await supabase
        .from("proposals")
        .insert({
          artwork_id: artworkId,
          institution: formData.auctionHouse,
          message: formData.message,
          proposal_type: "auction",
          send_method: "service",
          email: formData.email || null,
          status: "pending",
          created_at: new Date().toISOString(),
        })
        .select();

      if (error) throw error;

      const proposalId = data[0].id;
      navigate(`/payment/service-auction-proposal/${artworkId}`, {
        state: {
          proposalId,
          artworkTitle:
            artwork.title || t("auctionProposalPage.fallbacks.untitledArtwork"),
          institution: formData.auctionHouse,
          proposalType: "auction",
        },
      });
    } catch (err) {
      console.error("Error submitting auction proposal:", err);
      toast.error(
        t("auctionProposalPage.errors.submit", {
          message: err.message || t("auctionProposalPage.errors.unknown"),
        })
      );
    }
  };

  const artworkSummary = useMemo(() => {
    if (!artwork) return [];
    const dimensions = artwork.specifications?.dimensions;
    const formattedValue = artwork.value
      ? new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(artwork.value)
      : t("auctionProposalPage.fallbacks.notSpecifiedFeminine");

    return [
      {
        label: t("auctionProposalPage.summary.title"),
        value: artwork.title || t("auctionProposalPage.fallbacks.untitled"),
      },
      {
        label: t("auctionProposalPage.summary.artist"),
        value: artwork.artist || t("auctionProposalPage.fallbacks.unknownArtist"),
      },
      {
        label: t("auctionProposalPage.summary.year"),
        value: artwork.year || t("auctionProposalPage.fallbacks.notSpecifiedFeminine"),
      },
      {
        label: t("auctionProposalPage.summary.medium"),
        value: artwork.medium || t("auctionProposalPage.fallbacks.notSpecifiedMasculine"),
      },
      {
        label: t("auctionProposalPage.summary.dimensions"),
        value: dimensions
          ? `${dimensions.height || 0} × ${dimensions.width || 0} ${dimensions.unit || "cm"}`
          : t("auctionProposalPage.fallbacks.notSpecifiedPlural"),
      },
      {
        label: t("auctionProposalPage.summary.value"),
        value: formattedValue,
      },
    ];
  }, [artwork, locale, t]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12" aria-label={t("auctionProposalPage.loading")}>
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-blue-500 rounded-full" />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold text-red-600">
          {t("auctionProposalPage.notFound.title")}
        </h2>
        <p className="mt-2">{t("auctionProposalPage.notFound.description")}</p>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {t("auctionProposalPage.notFound.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">{t("auctionProposalPage.title")}</h1>

      <div className="bg-white p-5 rounded-lg shadow-sm border mb-6">
        <h2 className="text-xl font-semibold mb-4">
          {t("auctionProposalPage.artworkSection.title")}
        </h2>

        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
          {artwork.images && artwork.images.length > 0 && (
            <div className="md:w-1/3">
              <img
                src={artwork.images[0].url || artwork.images[0].path}
                alt={artwork.title || t("auctionProposalPage.fallbacks.artwork")}
                className="w-full rounded-md object-contain"
              />
            </div>
          )}

          <div className="md:w-2/3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {artworkSummary.map((item) => (
                <div key={item.label} className="border-b pb-2">
                  <div className="text-sm text-gray-600">{item.label}</div>
                  <div className="font-medium">{item.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex space-x-2">
              <button
                type="button"
                onClick={handlePreview}
                disabled={pdfGenerating}
                className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
              >
                <FiEye className="mr-1" aria-hidden="true" />
                {t("auctionProposalPage.actions.preview")}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={pdfGenerating}
                className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
              >
                {pdfGenerating ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-1 border-t-2 border-b-2 border-gray-700 rounded-full" />
                    {t("auctionProposalPage.actions.generating")}
                  </>
                ) : (
                  <>
                    <FiDownload className="mr-1" aria-hidden="true" />
                    {t("auctionProposalPage.actions.downloadPdf")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white p-5 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">
            {t("auctionProposalPage.form.title")}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block mb-2 font-medium">
                {t("auctionProposalPage.form.auctionHouse")}
              </label>
              <input
                type="text"
                name="auctionHouse"
                value={formData.auctionHouse}
                onChange={handleChange}
                placeholder={t("auctionProposalPage.form.auctionHousePlaceholder")}
                className="w-full border rounded-md px-4 py-2"
                required
              />
            </div>

            <div>
              <label className="block mb-2 font-medium">
                {t("auctionProposalPage.form.message")}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full border rounded-md px-4 py-2"
                placeholder={t("auctionProposalPage.form.messagePlaceholder")}
                required
              />
            </div>

            <div>
              <label className="block mb-2 font-medium">
                {t("auctionProposalPage.form.email")}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("auctionProposalPage.form.emailPlaceholder")}
                className="w-full border rounded-md px-4 py-2"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                {t("auctionProposalPage.form.emailHelp")}
              </p>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition-colors font-medium w-full"
        >
          {t("auctionProposalPage.actions.submit")}
        </button>
      </form>

      {previewMode && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">
                {t("auctionProposalPage.preview.title")}
              </h3>
              <button
                type="button"
                onClick={() => setPreviewMode(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label={t("auctionProposalPage.actions.closePreview")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div className="text-center">
                  <h1 className="text-2xl font-bold">
                    {t("auctionProposalPage.preview.heading")}
                  </h1>
                  <div className="text-xl font-semibold mt-2">{formData.auctionHouse}</div>
                  <p className="text-gray-600 mt-1">
                    {new Date().toLocaleDateString(locale)}
                  </p>
                </div>

                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={pdfGenerating}
                    className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {pdfGenerating ? (
                      <>
                        <span className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full" />
                        {t("auctionProposalPage.actions.generatingPdf")}
                      </>
                    ) : (
                      <>
                        <FiDownload className="mr-2" aria-hidden="true" />
                        {t("auctionProposalPage.actions.downloadFullPdf")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionProposal;
